import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { InnerLayout } from '../components/layout/InnerLayout';
import { DatastoreCard } from '../components/datastore/DatastoreCard';
import { Table2, Plus, Search } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';

export const ProjectDatastoresView: React.FC = () => {
  const { orgId: paramOrgId, projectId: paramProjectId } = useParams<{
    orgId?: string;
    projectId?: string;
  }>();
  const { currentOrg, currentProject, tables, isTablesLoading, canAdmin, canEdit, refreshTables } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');

  const orgId = paramOrgId || currentOrg?.id;
  const projectId = paramProjectId || currentProject?.id;

  useEffect(() => {
    if (projectId) {
      refreshTables(projectId);
    }
  }, [projectId, refreshTables]);

  const filteredTables = useMemo(() => {
    if (!searchQuery.trim()) return tables;
    const q = searchQuery.toLowerCase().trim();
    return tables.filter(
      (t) => t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q)
    );
  }, [tables, searchQuery]);

  return (
    <InnerLayout
      header={{
        title: `Datastores (${tables.length})`,
        description: `Dynamic datastores, ingestion endpoints, and spreadsheet views in ${currentProject?.name || 'this project'}.`,
        actions:
          canAdmin || canEdit
            ? [
                {
                  label: 'New Datastore',
                  icon: Plus,
                  variant: 'primary',
                  onClick: () => {
                    window.dispatchEvent(new CustomEvent('ericius:open-create-table'));
                  },
                },
              ]
            : undefined,
      }}
    >
      <div className="space-y-4">
        {tables.length > 4 && (
          <div className="flex items-center justify-between gap-4">
            <div className="w-full max-w-xs">
              <Input
                type="text"
                size="sm"
                leftIcon={<Search size={14} />}
                placeholder="Filter datastores..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {filteredTables.map((t) => (
            <DatastoreCard
              key={t.id}
              table={t}
              orgId={orgId}
              projectId={projectId}
            />
          ))}

          {tables.length === 0 && !isTablesLoading && (
            <div className="col-span-full py-16 px-4 text-center rounded-xl bg-cf-surface border border-cf-border">
              <div className="w-10 h-10 rounded-full bg-cf-bg border border-cf-border flex items-center justify-center mx-auto text-cf-textSecondary mb-2">
                <Table2 size={20} />
              </div>
              <div className="text-xs font-semibold text-cf-textPrimary uppercase tracking-wider">
                No Datastores Found
              </div>
              <p className="text-xs text-cf-textSecondary mt-1 max-w-sm mx-auto">
                Datastores accept incoming submissions and store them in dynamic JSONB columns with interactive spreadsheet controls.
              </p>
              {(canAdmin || canEdit) && (
                <Button
                  variant="primary"
                  size="md"
                  leftIcon={<Plus size={14} />}
                  onClick={() => window.dispatchEvent(new CustomEvent('ericius:open-create-table'))}
                  className="mt-4"
                >
                  Create Datastore
                </Button>
              )}
            </div>
          )}

          {tables.length > 0 && filteredTables.length === 0 && (
            <div className="col-span-full py-12 px-4 text-center rounded-xl bg-cf-surface border border-cf-border">
              <p className="text-xs text-cf-textSecondary">
                No datastores matching &ldquo;{searchQuery}&rdquo;.
              </p>
            </div>
          )}
        </div>
      </div>
    </InnerLayout>
  );
};
